// Appearances widget (Players canvas) — a per-match timeline: one row per match,
// a bar spanning the minutes on the pitch, with goal/yellow dots at their minute
// and a marker for a sending-off/injury exit. Old Catalyst showed this on the
// Bio tab; here it's its own widget. No `soccer-vis` (§7B decision 1): hand-drawn
// SVG. All the maths lives in the pure, unit-tested `appearancesTimeline.ts`.
//
// Consumes player + competition + season (appearances are comp+season scoped).

import { usePlayerAppearances } from "../data/players.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import {
  buildTimeline,
  type TimelineRow,
  type ExitKind,
} from "./appearancesTimeline.js";

// SVG coordinate frame. Width is fixed; the <svg> scales to the tile width and
// its height follows (uniform scale keeps the dots circular). The tile scrolls
// vertically when a full season's rows are taller than the widget.
const VIEW_W = 340;
const GUTTER = 44; // left column for the date label
const PLOT_R = VIEW_W - 8;
const ROW_H = 15;
const AXIS_H = 16;
const TOP = 6;

const COLOR = {
  started: theme.accent, // blue bar = started the match
  sub: "#c4b5fd", // violet bar = came on as a sub
  // Event dots are NEON (not mid-tone) so they pop off the pastel bars — a
  // deeper green / amber sat in the same tint range as the light-blue bar and
  // blurred into it. Emerald vs gold keeps the two dots clearly different hues
  // from each other; it also frees amber for the injury marker (distinct shape
  // AND colour from the yellow card).
  goal: "#00e676", // neon emerald green
  yellow: "#ffd600", // neon gold yellow (card)
  red: theme.danger,
  injury: "#f59e0b", // amber — distinct from the gold yellow card
  grid: "rgba(148,163,184,0.25)",
} as const;

// Dark ring around the event dots. A white ring vanished against the light-blue
// bar; a dark navy ring makes the dot pop crisply on any bar colour.
const DOT_RING = "#0b1220";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]}`;
}

export default function AppearancesWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const { data, isPending, isError, error } = usePlayerAppearances(playerId, competitionId, seasonId);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null) return <Msg>Pick a competition and season above.</Msg>;
  if (isPending) return <Msg>Loading appearances…</Msg>;
  if (isError) return <Msg danger>Couldn't load appearances. {String(error)}</Msg>;

  const t = buildTimeline(data.appearances);
  if (t.apps === 0) return <Msg>No appearances for this player in this season.</Msg>;

  const gridMinutes = t.axisMax >= 105 ? [45, 90, t.axisMax] : [45, 90];
  const plotH = t.rows.length * ROW_H;
  const svgH = TOP + plotH + AXIS_H;
  const x = (min: number) => GUTTER + (min / t.axisMax) * (PLOT_R - GUTTER);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
      <div style={{ color: theme.inkMuted, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
        <span style={{ color: theme.ink, fontWeight: 600 }}>{t.apps}</span> apps ·{" "}
        <span style={{ color: theme.ink, fontWeight: 600 }}>{t.starts}</span> starts ·{" "}
        {t.totalMinutes} min
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <svg
          viewBox={`0 0 ${VIEW_W} ${svgH}`}
          style={{ width: "100%", height: "auto", display: "block" }}
          role="img"
          aria-label={`Appearances timeline: ${t.apps} matches`}
        >
          {/* Minute gridlines + labels (drawn behind the bars). */}
          {gridMinutes.map((min) => (
            <g key={min}>
              <line x1={x(min)} y1={TOP} x2={x(min)} y2={TOP + plotH} stroke={COLOR.grid} strokeWidth={0.5} />
              <text x={x(min)} y={svgH - 4} fill={theme.inkFaint} fontSize={8} textAnchor="middle">
                {min}&apos;
              </text>
            </g>
          ))}
          {t.rows.map((row, i) => (
            <Row key={`${row.matchId}-${i}`} row={row} y={TOP + i * ROW_H} x={x} />
          ))}
        </svg>
      </div>

      <Legend hasRed={t.rows.some((r) => r.exit === "red" || r.exit === "injury")} />
    </div>
  );
}

function Row({ row, y, x }: { row: TimelineRow; y: number; x: (m: number) => number }) {
  const cy = y + ROW_H / 2;
  const barH = 6;
  const barColor = row.started ? COLOR.started : COLOR.sub;

  return (
    <g>
      <text x={GUTTER - 4} y={cy + 3} fill={theme.inkFaint} fontSize={8} textAnchor="end">
        {shortDate(row.date)}
      </text>

      {row.played ? (
        <rect
          x={x(row.on)}
          y={cy - barH / 2}
          width={Math.max(1, x(row.off) - x(row.on))}
          height={barH}
          rx={1.5}
          fill={barColor}
          fillOpacity={0.85}
        >
          <title>{rowTitle(row)}</title>
        </rect>
      ) : (
        // Unused sub: a hollow marker at kick-off, no bar.
        <circle cx={x(0)} cy={cy} r={2} fill="none" stroke={theme.inkFaint} strokeWidth={0.8}>
          <title>{rowTitle(row)}</title>
        </circle>
      )}

      {/* Sending-off / injury exit marker at the off minute. */}
      {(row.exit === "red" || row.exit === "injury") && (
        <rect
          x={x(row.off) - 1.5}
          y={cy - 4}
          width={3}
          height={8}
          fill={row.exit === "red" ? COLOR.red : COLOR.injury}
        >
          <title>{exitLabel(row.exit)} ({Math.floor(row.off)}&apos;)</title>
        </rect>
      )}

      {/* Goal / yellow dots at their minute, on top of the bar. Drawn bigger than
          the bar with a dark ring so they clearly pop (like old Catalyst). */}
      {row.events.map((e, j) => (
        <circle
          key={j}
          cx={x(e.minute)}
          cy={cy}
          r={4}
          fill={e.kind === "goal" ? COLOR.goal : COLOR.yellow}
          stroke={DOT_RING}
          strokeWidth={1}
        >
          <title>
            {e.kind === "goal" ? "Goal" : "Yellow card"} ({Math.floor(e.minute)}&apos;)
          </title>
        </circle>
      ))}
    </g>
  );
}

function rowTitle(row: TimelineRow): string {
  if (!row.played) return `${shortDate(row.date)} · in squad, no minutes`;
  const start = row.started ? "Started" : `Came on ${Math.floor(row.on)}'`;
  const end =
    row.exit === "sub"
      ? `subbed ${Math.floor(row.off)}'`
      : row.exit === "red"
        ? `sent off ${Math.floor(row.off)}'`
        : row.exit === "injury"
          ? `off injured ${Math.floor(row.off)}'`
          : "played to the end";
  return `${shortDate(row.date)} · ${start} → ${end} · ${row.minutes} min`;
}

function exitLabel(exit: ExitKind): string {
  return exit === "red" ? "Sent off" : "Injury";
}

function Legend({ hasRed }: { hasRed: boolean }) {
  const items: { color: string; label: string; shape?: "bar" | "dot" }[] = [
    { color: COLOR.started, label: "Started", shape: "bar" },
    { color: COLOR.sub, label: "Sub", shape: "bar" },
    { color: COLOR.goal, label: "Goal", shape: "dot" },
    { color: COLOR.yellow, label: "Yellow", shape: "dot" },
    ...(hasRed ? [{ color: COLOR.red, label: "Off (red/injury)", shape: "bar" as const }] : []),
  ];
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: theme.inkMuted }}>
      {items.map(({ color, label, shape }) => (
        <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              width: shape === "dot" ? 8 : 12,
              height: 8,
              borderRadius: shape === "dot" ? "50%" : 2,
              background: color,
              display: "inline-block",
            }}
          />
          {label}
        </span>
      ))}
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
